import { useEffect, useId } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import {
  GRADIENT_MARK_SX,
  PRIMARY_PILL_SX,
  QUIET_BUTTON_SX,
} from '@/components/answerStyle/answerStyle.styles';
import useAnalytics from '@/hooks/useAnalytics';
import { BORDER_RADIUS, semanticColors } from '@/theme/tokens';

interface AnswerStyleInviteProps {
  onChoose: () => void;
  onNotNow: () => void;
}

// A notch smaller than the dialog's actions: this is a nudge beside the
// composer, not a form.
const COMPACT = { minHeight: 32, fontSize: 12 } as const;

/**
 * The compact, dismissible "Answers, your way" invitation under the composer
 * on an empty chat — one slim row, a quieter sibling of the composer card. It
 * opens nothing on its own and blocks nothing: the voter can type, pick a
 * suggested prompt, or look up their ballot straight past it.
 * Mirrors the mobile `AnswerStyleInvite`.
 */
export default function AnswerStyleInvite({ onChoose, onNotNow }: AnswerStyleInviteProps) {
  const { t } = useTranslation('chat');
  const { track } = useAnalytics();
  const titleId = useId();

  useEffect(() => {
    track('Answer Style Invite Shown', { source: 'invite', surface: 'chat_landing' });
  }, [track]);

  return (
    <Box
      component="section"
      aria-labelledby={titleId}
      sx={{
        width: '100%',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        columnGap: '10px',
        rowGap: '8px',
        px: '12px',
        py: '10px',
        borderRadius: BORDER_RADIUS,
        // White with the composer's hairline: a quieter sibling of the card above
        // it, distinct from the page's light-blue background.
        backgroundColor: semanticColors.surface,
        border: `1px solid ${semanticColors.composerBorder}`,
        textAlign: 'start',
      }}
    >
      <Box sx={GRADIENT_MARK_SX} aria-hidden="true">
        <SparklesIcon style={{ width: 15, height: 15 }} />
      </Box>
      <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
        <Typography
          id={titleId}
          component="h2"
          sx={{
            fontSize: 13,
            fontWeight: 600,
            lineHeight: 1.35,
            color: semanticColors.textPrimary,
          }}
        >
          {t('answerStyleInvite.title')}
        </Typography>
        <Typography sx={{ fontSize: 12, lineHeight: 1.4, color: semanticColors.textCaption }}>
          {t('answerStyleInvite.body')}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', marginInlineStart: 'auto' }}>
        <Button onClick={onNotNow} sx={[QUIET_BUTTON_SX, COMPACT]}>
          {t('answerStyleInvite.notNow')}
        </Button>
        <Button
          onClick={onChoose}
          variant="contained"
          disableElevation
          sx={[PRIMARY_PILL_SX, COMPACT, { padding: '0 14px' }]}
        >
          {t('answerStyleInvite.choose')}
        </Button>
      </Box>
    </Box>
  );
}
